import type { PdfTemplateRow } from '@erp/data-access/interfaces';
import type { AnyDocType, DocScope, TemplateOptions, RenderData } from '@erp/pdf';
import type { PdfCompanyInfo } from '@erp/pdf';
import { generatePdf, generatePdfBatch, generatePdfDataUrl, getDocTitle } from '@erp/pdf';

const FALLBACK_TEMPLATE_DATE = '1970-01-01T00:00:00.000Z';

interface CompanySettingsForPdf {
  companyName?: string | null;
  taxId?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  logoUrl?: string | null;
  tagline?: string | null;
  subtitle?: string | null;
  website?: string | null;
  fax?: string | null;
  rib?: string | null;
  bankName?: string | null;
  socialFacebook?: string | null;
  socialInstagram?: string | null;
  socialLinkedin?: string | null;
}

function fallbackTemplateFor(scope: DocScope, docType: AnyDocType): PdfTemplateRow {
  return {
    id: `fallback-${scope}-${docType}`,
    docScope: scope,
    docType,
    name: getDocTitle(scope, docType),
    designKey: 'classic',
    options: {},
    isDefault: true,
    isActive: true,
    createdAt: FALLBACK_TEMPLATE_DATE,
    updatedAt: FALLBACK_TEMPLATE_DATE,
  };
}

export function activeTemplatesFor(
  templates: PdfTemplateRow[] | undefined,
  scope: DocScope,
  docType: AnyDocType,
): PdfTemplateRow[] {
  const activeTemplates = (templates ?? [])
    .filter((t) => t.docScope === scope && t.docType === docType && t.isActive)
    .sort((a, b) => {
      // Default first, then name
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  return activeTemplates.length > 0 ? activeTemplates : [fallbackTemplateFor(scope, docType)];
}

export function companyInfoForPdf(settings: CompanySettingsForPdf | null | undefined): PdfCompanyInfo {
  return {
    companyName: settings?.companyName || 'Entreprise',
    taxId: settings?.taxId ?? null,
    address: settings?.address ?? null,
    phone: settings?.phone ?? null,
    email: settings?.email ?? null,
    logoUrl: settings?.logoUrl ?? null,
    tagline: settings?.tagline ?? null,
    subtitle: settings?.subtitle ?? null,
    website: settings?.website ?? null,
    fax: settings?.fax ?? null,
    rib: settings?.rib ?? null,
    bankName: settings?.bankName ?? null,
    socialFacebook: settings?.socialFacebook ?? null,
    socialInstagram: settings?.socialInstagram ?? null,
    socialLinkedin: settings?.socialLinkedin ?? null,
  };
}

export function printWithTemplate(
  template: PdfTemplateRow,
  company: PdfCompanyInfo,
  data: RenderData,
  scope: DocScope,
  docType: AnyDocType,
): Promise<void> {
  return generatePdf({
    designKey: template.designKey,
    options: template.options as Partial<TemplateOptions>,
    company,
    data,
    docTypeTitle: getDocTitle(scope, docType),
  });
}

export function printBatchWithTemplate(
  template: PdfTemplateRow,
  company: PdfCompanyInfo,
  items: { data: RenderData; scope: DocScope; docType: AnyDocType }[],
): Promise<void> {
  return generatePdfBatch(
    items.map((it) => ({
      designKey: template.designKey,
      options: template.options as Partial<TemplateOptions>,
      company,
      data: it.data,
      docTypeTitle: getDocTitle(it.scope, it.docType),
    })),
  );
}

export function pdfUrlWithTemplate(
  template: PdfTemplateRow,
  company: PdfCompanyInfo,
  data: RenderData,
  scope: DocScope,
  docType: AnyDocType,
): Promise<string> {
  return generatePdfDataUrl({
    designKey: template.designKey,
    options: template.options as Partial<TemplateOptions>,
    company,
    data,
    docTypeTitle: getDocTitle(scope, docType),
  });
}
